import React from 'react';
import { ChangeType } from 'apollo/generated/client-operations';
import { Dropzone, InputFile } from 'components/dropzone';
import { isServerFile } from 'utils';
import { FormSubmissionInlineDocumentProps } from './types';

export const FormSubmissionInlineDocument = React.forwardRef(
  (
    props: FormSubmissionInlineDocumentProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ): React.ReactElement => {
    const { value, onChange, name, associationType, ...restProps } = props;

    const getChangedFiles = (newFiles: InputFile[]): typeof value['files'] => {
      // existing server files which were not removed
      const existingFileIds = newFiles
        ?.filter((file) => isServerFile(file))
        .map((file) => file.fileId);
      const removedFiles: typeof value['files'] = value?.files
        .filter(
          // existing server files which were removed
          (file) => file.fileId && !existingFileIds?.includes(file.fileId),
        )
        .map((file) => ({
          fileId: file.fileId,
          changeType: ChangeType.Removed,
        }));

      const createdFiles: typeof value['files'] = newFiles
        ?.filter((file) => !isServerFile(file))
        .map((file) => Object.assign(file, { changeType: ChangeType.Created }));

      const changedFiles = [...(removedFiles ?? []), ...(createdFiles ?? [])];
      return changedFiles.length ? changedFiles : [];
    };

    const files = value?.files?.filter(
      (file): file is InputFile => file.changeType !== ChangeType.Removed,
    );

    return (
      <Dropzone
        name={name}
        multiple
        maxFiles={1}
        value={files ?? null}
        onChange={(newFiles) => {
          if (value?.changeType === ChangeType.Created && !newFiles) {
            // If the change type is 'created' and there are no new files,
            // we need to reset the value to `null`.
            onChange?.(null);
          }

          let newChangeType: typeof value['changeType'];
          if (newFiles) {
            newChangeType = value ? ChangeType.Updated : ChangeType.Created;
          } else if (value) {
            newChangeType = ChangeType.Removed;
          }

          if (newChangeType) {
            onChange?.({
              associationType,
              ...value,
              files: getChangedFiles(newFiles),
              changeType: newChangeType,
            });
          }
        }}
        ref={ref}
        {...restProps}
      />
    );
  },
);
